/**
 * Cart summary component for the main cart page
 * 
 * Handles promotional code application and cart note.
 * Uses Shopify's Cart AJAX API to validate and apply discount codes.
 * 
 * @module cart-summary
 * @version 1.2.0
 * @extends HTMLElement
 */

(function() {
    const COMPONENT_NAME = 'cart-summary';

    if (!customElements.get(COMPONENT_NAME)) {
        class CartSummary extends HTMLElement {

            constructor() {
                super();
                this.discountCode = null;
                this.isLoading = false;
                this.noteDebounceTimer = null;
            }

            connectedCallback() {
                this.discountInput = this.querySelector('[ref="discountInput"]');
                this.discountMessage = this.querySelector('[ref="discountMessage"]');
                this.appliedDiscount = this.querySelector('[ref="appliedDiscount"]');
                this.discountCodeDisplay = this.querySelector('[ref="discountCodeDisplay"]');
                this.applyButton = this.querySelector('[data-apply-discount]');
                this.removeButton = this.querySelector('[data-remove-discount]');
                this.cartNoteTextarea = this.querySelector('[data-cart-note]');

                // Bind events
                this.bindEvents();
            }

            bindEvents() {
                // Apply discount button
                if (this.applyButton) {
                    this.applyButton.addEventListener('click', this.applyDiscount.bind(this));
                }

                // Remove discount buttons (can be multiple)
                this.querySelectorAll('[data-remove-discount]').forEach(button => {
                    button.addEventListener('click', this.removeDiscount.bind(this));
                });

                // Cart note auto-save with debounce
                if (this.cartNoteTextarea) {
                    this.cartNoteTextarea.addEventListener('input', this.handleNoteChange.bind(this));
                }

                // Allow Enter key on discount input
                if (this.discountInput) {
                    this.discountInput.addEventListener('keypress', (e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            this.applyDiscount();
                        }
                    });
                }
            }

            /**
             * Handle cart note changes with debounce
             */
            handleNoteChange() {
                clearTimeout(this.noteDebounceTimer);
                this.noteDebounceTimer = setTimeout(() => {
                    this.updateCartNote();
                }, 500);
            }

            /**
             * Update cart note via AJAX
             */
            async updateCartNote() {
                const note = this.cartNoteTextarea?.value || '';
                
                try {
                    await fetch(window.Shopify.routes.root + 'cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ note })
                    });
                } catch (error) {
                    console.error('Error updating cart note:', error);
                }
            }

            /**
             * Set loading state on the apply button
             * 
             * @param {boolean} loading - Whether to show loading state
             */
            setLoading(loading) {
                this.isLoading = loading;
                
                if (this.applyButton) {
                    if (loading) {
                        this.applyButton.disabled = true;
                        this.applyButton.textContent = 'Applying...';
                    } else {
                        this.applyButton.disabled = false;
                        this.applyButton.textContent = 'Apply';
                    }
                }
            }

            /**
             * Apply discount code using Shopify Cart AJAX API
             */
            async applyDiscount() {
                const code = this.discountInput?.value?.trim();
                
                if (!code) {
                    this.showMessage('Please enter a discount code', 'error');
                    return;
                }

                if (this.isLoading) return;

                this.setLoading(true);

                try {
                    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            discount: code
                        })
                    });

                    const cart = await response.json();

                    if (!response.ok) {
                        throw new Error(cart.description || cart.message || 'Failed to apply discount');
                    }

                    // Check if discount was applied
                    const hasDiscount = cart.cart_level_discount_applications?.length > 0 || 
                                       cart.total_discount > 0;

                    if (hasDiscount) {
                        this.discountCode = code.toUpperCase();
                        this.showMessage('Discount code applied successfully!', 'success');
                        this.showAppliedDiscount(this.discountCode);
                        
                        // Clear input
                        if (this.discountInput) {
                            this.discountInput.value = '';
                        }

                        // Close accordion
                        const accordion = this.querySelector('component-accordion');
                        if (accordion && typeof accordion.close === 'function') {
                            accordion.close();
                        }

                        // Refresh the page to show updated prices
                        this.refreshCartSection(cart);
                    } else {
                        // Discount code might be invalid or not applicable
                        this.showMessage('Discount code is not valid or not applicable', 'error');
                    }

                } catch (error) {
                    console.error('Error applying discount:', error);
                    this.showMessage(error.message || 'Failed to apply discount code', 'error');
                } finally {
                    this.setLoading(false);
                }
            }

            /**
             * Remove discount code using Shopify Cart AJAX API
             */
            async removeDiscount() {
                if (this.isLoading) return;

                this.setLoading(true);

                try {
                    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            discount: '' // Empty string removes all discounts
                        })
                    });

                    const cart = await response.json();

                    if (!response.ok) {
                        throw new Error(cart.description || cart.message || 'Failed to remove discount');
                    }

                    this.discountCode = null;
                    this.hideAppliedDiscount();
                    this.showMessage('Discount code removed', 'success');

                    // Refresh the page to show updated prices
                    this.refreshCartSection(cart);

                } catch (error) {
                    console.error('Error removing discount:', error);
                    this.showMessage(error.message || 'Failed to remove discount code', 'error');
                } finally {
                    this.setLoading(false);
                }
            }

            /**
             * Refresh the cart section to show updated prices
             * 
             * @param {Object} cart - The cart data from API response
             */
            refreshCartSection(cart) {
                // Update cart total if element exists
                const cartTotal = this.querySelector('[data-cart-subtotal]');
                if (cartTotal && cart.total_price !== undefined) {
                    cartTotal.textContent = this.formatMoney(cart.total_price);
                }

                // Dispatch event for other components
                window.dispatchEvent(new CustomEvent('cart-change', { detail: { cart } }));

                // Reload page to update all cart displays with proper Liquid rendering
                window.location.reload();
            }

            /**
             * Format money value (cents to formatted string)
             * 
             * @param {number} cents - Amount in cents
             * @returns {string} Formatted money string
             */
            formatMoney(cents) {
                return window.theme.money.format(cents);
            }

            /**
             * Show applied discount display
             * 
             * @param {string} code - The discount code
             */
            showAppliedDiscount(code) {
                if (this.appliedDiscount && this.discountCodeDisplay) {
                    this.discountCodeDisplay.textContent = code;
                    this.appliedDiscount.classList.remove('hidden');
                }
            }

            /**
             * Hide applied discount display
             */
            hideAppliedDiscount() {
                if (this.appliedDiscount) {
                    this.appliedDiscount.classList.add('hidden');
                }
            }

            /**
             * Show message in the discount form
             * 
             * @param {string} message - The message to display
             * @param {string} type - Message type: 'success' or 'error'
             */
            showMessage(message, type = 'success') {
                if (this.discountMessage) {
                    this.discountMessage.textContent = message;
                    this.discountMessage.classList.remove('hidden', 'text-green-600', 'text-red-600');
                    this.discountMessage.classList.add(type === 'success' ? 'text-green-600' : 'text-red-600');
                    
                    // Auto hide after 5 seconds
                    setTimeout(() => {
                        this.discountMessage.classList.add('hidden');
                    }, 5000);
                }
            }

        }

        customElements.define(COMPONENT_NAME, CartSummary);
    }
})();

